import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Clock, ChevronRight, Phone, ArrowRight, ArrowLeft, Share2, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getMoldGuideBySlug, getRelatedGuides } from '../data/moldGuidesData';
import { businessConfig } from '../data/businessConfig';
import SEOHead from '../components/SEOHead';

const GuidePage = () => {
  const { slug } = useParams();
  const guide = getMoldGuideBySlug(slug);
  
  if (!guide) {
    return <Navigate to="/guides" replace />;
  }

  const relatedGuides = getRelatedGuides(slug);

  // Convert markdown-like content to JSX
  const renderContent = (content) => {
    const lines = content.trim().split('\n');
    const elements = [];
    let listItems = [];
    let inList = false;

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      if (trimmed.startsWith('## ')) {
        if (inList && listItems.length) {
          elements.push(<ul key={`ul-${index}`} className="list-disc pl-6 space-y-2 mb-6">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(
          <h2 key={index} className="text-2xl font-bold text-neutral-900 mt-10 mb-4">
            {trimmed.replace('## ', '')}
          </h2>
        );
      } else if (trimmed.startsWith('### ')) {
        if (inList && listItems.length) {
          elements.push(<ul key={`ul-${index}`} className="list-disc pl-6 space-y-2 mb-6">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(
          <h3 key={index} className="text-xl font-semibold text-neutral-900 mt-8 mb-3">
            {trimmed.replace('### ', '')}
          </h3>
        );
      } else if (trimmed.startsWith('#### ')) {
        if (inList && listItems.length) {
          elements.push(<ul key={`ul-${index}`} className="list-disc pl-6 space-y-2 mb-6">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(
          <h4 key={index} className="text-lg font-semibold text-neutral-800 mt-6 mb-2">
            {trimmed.replace('#### ', '')}
          </h4>
        );
      } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        inList = true;
        const text = trimmed.replace(/^[-*] /, '');
        // Handle bold text
        const formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        listItems.push(
          <li key={index} className="text-neutral-700" dangerouslySetInnerHTML={{ __html: formattedText }} />
        );
      } else if (trimmed.match(/^\d+\. /)) {
        if (inList && listItems.length) {
          elements.push(<ul key={`ul-${index}`} className="list-disc pl-6 space-y-2 mb-6">{listItems}</ul>);
          listItems = [];
        }
        inList = true;
        const text = trimmed.replace(/^\d+\. /, '');
        const formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        listItems.push(
          <li key={index} className="text-neutral-700" dangerouslySetInnerHTML={{ __html: formattedText }} />
        );
      } else if (trimmed === '') {
        if (inList && listItems.length) {
          elements.push(<ul key={`ul-${index}`} className="list-disc pl-6 space-y-2 mb-6">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
      } else if (trimmed) {
        if (inList && listItems.length) {
          elements.push(<ul key={`ul-${index}`} className="list-disc pl-6 space-y-2 mb-6">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        // Handle bold text in paragraphs
        const formattedText = trimmed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        elements.push(
          <p key={index} className="text-neutral-700 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: formattedText }} />
        );
      }
    });

    // Handle any remaining list items
    if (listItems.length) {
      elements.push(<ul key="final-ul" className="list-disc pl-6 space-y-2 mb-6">{listItems}</ul>);
    }

    return elements;
  };

  // Schema for article
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": guide.title,
    "description": guide.metaDescription,
    "image": guide.heroImage,
    "datePublished": guide.lastUpdated,
    "dateModified": guide.lastUpdated,
    "author": {
      "@type": "Organization",
      "name": "Gray Contracting",
      "url": businessConfig.baseUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "Gray Contracting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_gray-remodel-seo/artifacts/bd5e3tge_company%20logo%202020%20%281%29.jpg"
      }
    }
  };

  return (
    <div>
      <SEOHead 
        title={guide.metaTitle}
        description={guide.metaDescription}
        canonical={`/guides/${guide.slug}`}
        ogImage={guide.heroImage}
      />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />

      {/* Hero */}
      <section className="relative py-16 bg-neutral-900">
        <div className="absolute inset-0">
          <img 
            src={guide.heroImage} 
            alt={guide.title}
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-neutral-900/80 to-neutral-900/60" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-neutral-400 mb-6">
            <Link to="/" className="hover:text-amber-400">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/guides" className="hover:text-amber-400">Guides</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-amber-400 truncate">{guide.title}</span>
          </nav>
          
          <div className="flex items-center gap-4 text-sm text-neutral-400 mb-4">
            <span className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full">{guide.category}</span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {guide.readTime}
            </span>
            <span>Updated {guide.lastUpdated}</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {guide.title}
          </h1>
          <p className="text-lg text-neutral-300">
            {guide.metaDescription}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <article className="lg:col-span-3 prose prose-neutral max-w-none">
              {renderContent(guide.content)}

              {/* CTA Box */}
              <div className="mt-12 bg-amber-50 border-2 border-amber-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-neutral-900 mb-3">Need Professional Help?</h3>
                <p className="text-neutral-700 mb-4">
                  Gray Contracting provides licensed mold remediation and reconstruction services throughout Dallas-Fort Worth. 
                  Texas License #1776.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a href={`tel:${businessConfig.phoneTel}`}>
                    <Button className="bg-amber-500 hover:bg-amber-600 text-white w-full sm:w-auto">
                      <Phone className="w-4 h-4 mr-2" />
                      Call {businessConfig.phone}
                    </Button>
                  </a>
                  <Link to="/services/mold-remediation">
                    <Button variant="outline" className="w-full sm:w-auto">
                      Request Estimate
                    </Button>
                  </Link>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Related Guides */}
                {relatedGuides.length > 0 && (
                  <div className="bg-neutral-50 rounded-xl p-5">
                    <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-amber-500" />
                      Related Guides
                    </h3>
                    <div className="space-y-3">
                      {relatedGuides.map(related => (
                        <Link 
                          key={related.slug}
                          to={`/guides/${related.slug}`}
                          className="block text-sm text-neutral-700 hover:text-amber-600 transition-colors"
                        >
                          {related.title} →
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Contact */}
                <div className="bg-neutral-900 rounded-xl p-5 text-white">
                  <h3 className="font-semibold mb-3">Questions?</h3>
                  <p className="text-sm text-neutral-300 mb-4">
                    We're here to help with your mold questions.
                  </p>
                  <a href={`tel:${businessConfig.phoneTel}`} className="block">
                    <Button className="w-full bg-amber-500 hover:bg-amber-600">
                      <Phone className="w-4 h-4 mr-2" />
                      {businessConfig.phone}
                    </Button>
                  </a>
                </div>
              </div>
            </aside>
          </div>

          {/* Navigation */}
          <div className="mt-12 pt-8 border-t border-neutral-200 flex justify-between">
            <Link to="/guides" className="flex items-center gap-2 text-neutral-600 hover:text-amber-600">
              <ArrowLeft className="w-4 h-4" />
              All Guides
            </Link>
            <Link to="/services/mold-remediation" className="flex items-center gap-2 text-amber-600 font-medium hover:text-amber-700">
              View Mold Services
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GuidePage;
